import * as THREE from 'three/webgpu';
import { KeyboardBindings } from './KeyboardBindings';
import { MouseHandler } from './MouseHandler';
import { TargetSelector } from './TargetSelector';
import { CommandType, type PlayerCommand, type Vec3 } from './types';
import { isAdminMode } from '../util/url';

export type CommandCallback = (command: PlayerCommand) => void;
export type DroneHoverCallback = (droneId: string | null) => void;

/** Throttle the hover raycast to ~10Hz so it stays cheap even with many drones. */
const HOVER_RAYCAST_INTERVAL_MS = 100;

export class InputManager {
    private keyboard: KeyboardBindings;
    private mouse: MouseHandler;
    private targetSelector: TargetSelector;
    private commandCallback: CommandCallback | null = null;
    private cameraOrbitCb: ((dx: number, dy: number) => void) | null = null;
    private cameraZoomCb: ((d: number) => void) | null = null;
    private abilityTargetResolver: ((slot?: number) => string | null) | null = null;
    private enabled = false;
    private cameraOrbitAngle = 0;
    private wasMoving = false;
    /** Last-known cursor screen coords; updated on mousemove for hover raycasts. */
    private hoverScreenX = -1;
    private hoverScreenY = -1;
    /** ms since the last hover raycast was performed. */
    private hoverElapsedMs = 0;
    /** Currently hovered drone id, or '' when nothing is under the cursor. */
    private hoveredDroneId = '';
    private hoverCallback: DroneHoverCallback | null = null;
    private hoverMoveListener: ((e: MouseEvent) => void) | null = null;

    /**
     * True when the page was loaded with `?admin=true`. Gates the Ctrl+D /
     * Ctrl+K / Ctrl+R debug hotkeys so non-admin sessions never accidentally
     * dispatch a command that the server would reject anyway.
     * Resolved once at construction so unit tests can override via DI.
     */
    private adminMode: boolean;

    constructor(
        sceneManager: { getCamera(): THREE.PerspectiveCamera; getRenderer(): THREE.WebGPURenderer; getScene(): THREE.Scene },
        opts: { adminMode?: boolean } = {},
    ) {
        const renderer = sceneManager.getRenderer();
        const camera = sceneManager.getCamera();
        const scene = sceneManager.getScene();

        this.keyboard = new KeyboardBindings();
        this.mouse = new MouseHandler(renderer.domElement);
        this.targetSelector = new TargetSelector(camera, null, scene, renderer.domElement);

        // Caller can override (used by tests). Default detects `?admin=true`
        // on the live page so production builds keep the URL-flag contract.
        this.adminMode = opts.adminMode ?? isAdminMode();

        // Track the cursor on the renderer's canvas so the per-tick hover
        // raycast knows where to fire. The raw move listener is intentionally
        // separate from `MouseHandler` (which is concerned with click + orbit
        // gestures) so adding hover doesn't ripple into click semantics.
        this.hoverMoveListener = (e: MouseEvent) => {
            this.hoverScreenX = e.clientX;
            this.hoverScreenY = e.clientY;
        };

        this.keyboard.onKeyAction((action, slot) => {
            if (!this.enabled || !this.commandCallback) return;
            if (action === 'activate_card' && slot !== undefined) {
                this.commandCallback({ type: CommandType.ActivateCard, cardSlot: slot });
            } else if (action === 'cancel') {
                this.commandCallback({ type: CommandType.CancelOrder });
            } else if (action === 'trigger_ability') {
                const droneId = this.abilityTargetResolver?.();
                if (droneId) {
                    this.commandCallback({ type: CommandType.TriggerAbility, droneId, direction: this.getActionForwardDirection() });
                }
            } else if (action === 'trigger_ability_slot' && slot !== undefined) {
                const droneId = this.abilityTargetResolver?.(slot);
                if (droneId) {
                    this.commandCallback({ type: CommandType.TriggerAbility, droneId, direction: this.getActionForwardDirection() });
                }
            } else if (action === 'fire_basic') {
                this.commandCallback({ type: CommandType.FireBasic, direction: this.getActionForwardDirection() });
            } else if (action === 'admin_spawn_dummy' && this.adminMode) {
                this.commandCallback({ type: CommandType.AdminSpawnDummy });
            } else if (action === 'admin_damage_self' && this.adminMode) {
                this.commandCallback({ type: CommandType.AdminDamageSelf });
            } else if (action === 'admin_reset_arena' && this.adminMode) {
                this.commandCallback({ type: CommandType.AdminResetArena });
            }
        });

        this.mouse.onClick((sx, sy) => {
            if (!this.enabled || !this.commandCallback) return;
            const result = this.targetSelector.select(sx, sy);
            if (result.type !== 'none') {
                this.commandCallback({
                    type: CommandType.SetTarget,
                    targetId: result.id || '',
                    worldPos: result.worldPos,
                });
            }
        });

        this.mouse.onDrag((dx, dy) => { this.cameraOrbitCb?.(dx, dy); });
        this.mouse.onScroll((d) => { this.cameraZoomCb?.(d); });
    }

    onCommand(cb: CommandCallback): void { this.commandCallback = cb; }
    onCameraOrbit(cb: (dx: number, dy: number) => void): void { this.cameraOrbitCb = cb; }
    onCameraZoom(cb: (d: number) => void): void { this.cameraZoomCb = cb; }
    onAbilityTarget(resolver: (slot?: number) => string | null): void { this.abilityTargetResolver = resolver; }
    /**
     * Register a callback fired whenever the hovered drone id changes. Pass
     * the drone id when the cursor enters a local-player drone; pass `null`
     * when the cursor leaves all drones. The callback is fired exactly on
     * transitions — no per-frame spam for stable hover.
     */
    onDroneHover(cb: DroneHoverCallback | null): void { this.hoverCallback = cb; }

    /** Read-only diagnostic for tests. */
    getHoveredDroneId(): string { return this.hoveredDroneId; }

    update(_deltaMs: number): void {
        if (!this.enabled || !this.commandCallback) return;
        const dir = this.getMoveDirection();
        const isMoving = dir.x !== 0 || dir.y !== 0 || dir.z !== 0;
        if (isMoving || this.wasMoving) {
            this.commandCallback({ type: CommandType.Move, direction: dir, speed: isMoving ? 1.0 : 0 });
        }
        this.wasMoving = isMoving;
        this.tickHover(_deltaMs);
    }

    /**
     * Throttled hover raycast. Reuses `TargetSelector.select()` so the same
     * intersection logic that powers click selection drives the hover state —
     * one source of truth, no separate raycaster bookkeeping.
     */
    private tickHover(deltaMs: number): void {
        if (this.hoverScreenX < 0) return;
        this.hoverElapsedMs += deltaMs;
        if (this.hoverElapsedMs < HOVER_RAYCAST_INTERVAL_MS) return;
        this.hoverElapsedMs = 0;

        const result = this.targetSelector.select(this.hoverScreenX, this.hoverScreenY);
        const newId = result.type === 'drone' && result.id ? result.id : '';
        if (newId !== this.hoveredDroneId) {
            this.hoveredDroneId = newId;
            this.hoverCallback?.(newId === '' ? null : newId);
        }
    }

    /**
     * Forward a synthetic hover position. Exposed for tests so they can drive
     * `tickHover()` without touching window-level mouse events.
     */
    setHoverScreenPosForTest(x: number, y: number): void {
        this.hoverScreenX = x;
        this.hoverScreenY = y;
        // Force the next `tickHover` to run by zeroing the throttle accumulator.
        this.hoverElapsedMs = HOVER_RAYCAST_INTERVAL_MS;
    }

    setCameraOrbitAngle(angle: number): void {
        this.cameraOrbitAngle = angle;
    }

    getMoveDirection(): Vec3 {
        const keys = this.keyboard.getState();
        let ix = 0, iz = 0;
        if (keys.forward) iz -= 1;
        if (keys.backward) iz += 1;
        if (keys.left) ix -= 1;
        if (keys.right) ix += 1;
        const dy = (keys.up ? 1 : 0) - (keys.down ? 1 : 0);

        if (ix === 0 && iz === 0 && dy === 0) return { x: 0, y: 0, z: 0 };

        // FPS-style camera-relative WASD: W moves toward camera-look direction,
        // D strafes screen-right. Closed form of (ix·right + (-iz)·forward) using
        // forward = (-cos(a), 0, -sin(a)) and right = (-sin(a), 0, cos(a)) in the
        // Three.js right-handed convention. See InputManagerMovement.test for the
        // angle-by-angle verification.
        const cos = Math.cos(this.cameraOrbitAngle);
        const sin = Math.sin(this.cameraOrbitAngle);
        const d = {
            x: ix * sin + iz * cos,
            y: dy,
            z: -ix * cos + iz * sin,
        };
        const l = Math.sqrt(d.x * d.x + d.y * d.y + d.z * d.z);
        if (l > 0) { d.x /= l; d.y /= l; d.z /= l; }
        return d;
    }

    getActionForwardDirection(): Vec3 {
        const move = this.getMoveDirection();
        if (move.x !== 0 || move.y !== 0 || move.z !== 0) return move;

        return {
            x: -Math.cos(this.cameraOrbitAngle),
            y: 0,
            z: -Math.sin(this.cameraOrbitAngle),
        };
    }

    setEnabled(e: boolean): void {
        this.enabled = e;
        if (e) {
            this.keyboard.attach();
            this.mouse.attach();
            // Listen for raw cursor moves on the canvas. We intentionally use
            // `document` so cursor coords stay current even when the cursor
            // briefly exits the canvas during fast drags.
            if (this.hoverMoveListener && typeof document !== 'undefined') {
                document.addEventListener('mousemove', this.hoverMoveListener as EventListener);
            }
        } else {
            this.keyboard.detach();
            this.mouse.detach();
            if (this.hoverMoveListener && typeof document !== 'undefined') {
                document.removeEventListener('mousemove', this.hoverMoveListener as EventListener);
            }
            // Clear hover state so a re-enabled session doesn't carry stale
            // highlight from a previous enable cycle.
            if (this.hoveredDroneId !== '') {
                this.hoveredDroneId = '';
                this.hoverCallback?.(null);
            }
            this.hoverScreenX = -1;
            this.hoverScreenY = -1;
            // Reset all key states to prevent stuck keys
            const state = this.keyboard.getState();
            state.forward = false;
            state.backward = false;
            state.left = false;
            state.right = false;
            state.up = false;
            state.down = false;
            state.cancel = false;
            for (let i = 0; i < state.cardSlots.length; i++) {
                state.cardSlots[i] = false;
            }
            // Send a zero-direction stop command
            if (this.commandCallback) {
                this.commandCallback({ type: CommandType.Move, direction: { x: 0, y: 0, z: 0 }, speed: 0 });
            }
        }
    }

    setDroneGroup(group: THREE.Group): void {
        this.targetSelector.setDroneGroup(group);
    }

    /** Diagnostic for tests. */
    isAdminMode(): boolean { return this.adminMode; }
}
